/**
 * Booking Page — Public
 *
 * Collects parent + child info and initiates a $200 Stripe Checkout session.
 * No authentication required. After payment, redirects to /register.
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

interface FormState {
  parentName: string;
  email: string;
  phone: string;
  childName: string;
  childAge: string;
}

const inputClasses =
  'w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#8E8E93] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/30 focus:border-[#1A1A1A] transition-colors text-sm';

const labelClasses = 'block text-sm font-medium text-[#636366] mb-2';

export function BookPage() {
  const [form, setForm] = useState<FormState>({
    parentName: '',
    email: '',
    phone: '',
    childName: '',
    childAge: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const { parentName, email, phone, childName, childAge } = form;
    if (!parentName.trim() || !email.trim() || !phone.trim() || !childName.trim() || !childAge) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-book-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentName, email, phone, childName, childAge }),
      });

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#1C1C1E] mb-2" style={{ fontFamily: "'Roboto Slab', serif" }}>
            Book a Session
          </h1>
          <p className="text-[#636366] text-sm">
            One session · $200 · Secure checkout via Stripe
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.06] p-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              {/* Parent section */}
              <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">About you</p>

              <div>
                <label htmlFor="parentName" className={labelClasses}>Your name</label>
                <input
                  id="parentName"
                  name="parentName"
                  type="text"
                  autoComplete="name"
                  placeholder="Jane Smith"
                  value={form.parentName}
                  onChange={handleChange}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className={labelClasses}>Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="jane@example.com"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className={labelClasses}>Phone number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputClasses}
                  required
                />
              </div>

              {/* Child section */}
              <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider pt-2">About your child</p>

              <div>
                <label htmlFor="childName" className={labelClasses}>Child's name</label>
                <input
                  id="childName"
                  name="childName"
                  type="text"
                  placeholder="Alex"
                  value={form.childName}
                  onChange={handleChange}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label htmlFor="childAge" className={labelClasses}>Child's age</label>
                <select
                  id="childAge"
                  name="childAge"
                  value={form.childAge}
                  onChange={handleChange}
                  className={inputClasses}
                  required
                >
                  <option value="">Select age</option>
                  {Array.from({ length: 13 }, (_, i) => i + 3).map(age => (
                    <option key={age} value={String(age)}>{age} years old</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-[#FF3B30] bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#111111] text-[#1A1A1A] rounded-xl font-semibold text-sm hover:bg-[#111111]/90 active:bg-[#111111]/80 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to checkout…
                </>
              ) : (
                'Book session — $200'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#8E8E93] mt-4">
          You'll create your account after payment.
        </p>
      </div>
    </div>
  );
}
